import { buildSenderEmail, type UpdateQueueSettingsInput } from '@easyhelp/shared';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Controller, useWatch, type Control } from 'react-hook-form';
import { SettingsSection } from './SettingsSection';

export interface SenderSectionProps {
  control: Control<UpdateQueueSettingsInput>;
  /** Dominio della posta dei ticket, fisso per istanza (ADR-003). */
  mailDomain: string;
  /** Schema degli indirizzi a cui arrivano le repliche (`ticket+{codice}@dominio`). */
  replyToPattern: string;
}

/** Casella mittente: nome visualizzato e parte locale; il dominio non si cambia da qui. */
export function SenderSection({ control, mailDomain, replyToPattern }: SenderSectionProps) {
  const localPart = useWatch({ control, name: 'senderLocalPart' });
  return (
    <SettingsSection
      id="queue-settings-sender"
      title="Casella mittente"
      description="Indirizzo da cui partono le risposte e a cui arrivano le repliche di chi ha segnalato."
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
        <Controller
          name="senderName"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label="Nome visualizzato"
              required
              fullWidth
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message ?? 'Compare accanto all’indirizzo nel client di posta.'}
            />
          )}
        />
        <Controller
          name="senderLocalPart"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label="Casella"
              required
              fullWidth
              error={Boolean(fieldState.error)}
              helperText={
                fieldState.error?.message ??
                `Le risposte partono da ${buildSenderEmail(field.value.trim() || '…', mailDomain)}. Il dominio è fisso.`
              }
              slotProps={{
                input: { endAdornment: <InputAdornment position="end">@{mailDomain}</InputAdornment> },
              }}
            />
          )}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary">
        Le repliche arrivano a <code>{replyToPattern}</code>: il codice nell’indirizzo le collega al ticket giusto,
        qualunque sia la casella mittente
        {localPart.trim() ? ` (${buildSenderEmail(localPart.trim(), mailDomain)})` : ''}.
      </Typography>
    </SettingsSection>
  );
}
